import { Component, computed, HostListener, OnDestroy, OnInit, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';

import { MatSnackBar } from '@angular/material/snack-bar';
import {
  combineLatest,
  filter,
  forkJoin,
  map, Observable,
  Subject,
  takeUntil
} from 'rxjs';

import {
  LgbtIdentifiedOptions,
  PersonaProviderProfile,
  PersonaProviderStore
} from '@sondermind/data-access/provider-facing/persona-provider';
import { PartialProviderWithId, ProviderUserService } from '@sondermind/data-access/provider-user';
import { ReferenceDataHttpService, ReferenceDataTypes, ReferenceDatum } from '@sondermind/data-access/reference-data';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { IrisButtonTypes } from '@sondermind/iris/button';

import { DropdownOption } from '@sondermind/iris/dropdown';
import { RadioOption } from '@sondermind/iris/radio-group';

import { IAPIFetchResponse, ServerResponse } from '@sondermind/utilities/models-http';

import { ProviderUser } from '@sondermind/utilities/models-user-provider';
import { EthnicityDatum, ProviderEthnicitiesService } from '@sondermind/data-access/provider-facing/provider-profile';
import { IComponentCanDeactivate } from '@sondermind/features/provider-facing/provider-profile';

interface IGenderForm {
  id: FormControl<number>;
  genderId: FormControl<number>;
}

@Component({
  selector: 'member-page-identity',
  templateUrl: './profile-identity.component.html',
  styleUrls: ['./profile-identity.component.scss', '../../modules/profile/profile-app-shared-styles.scss'],
  standalone: false
})
export class ProfileIdentityPage implements OnInit, OnDestroy, IComponentCanDeactivate {
  user: ProviderUser;
  providerProfile: PersonaProviderProfile;
  saving: boolean = false;

  ethnicityForm: UntypedFormGroup;
  filteredEthnicites: ReferenceDatum[] = [];
  userEthnicities: EthnicityDatum[] = [];

  genderForm: FormGroup<IGenderForm>;
  ethnicityFormReset: any;
  genderOptions: Array<DropdownOption<number>> = [];

  lgbtIdentifiedOptions: Array<RadioOption<string>> = [
    { name: 'Yes', value: LgbtIdentifiedOptions.YES },
    { name: 'No', value: LgbtIdentifiedOptions.NO },
    { name: 'Prefer not to say', value: LgbtIdentifiedOptions.PREFER_NOT_TO_SAY }
  ];
  lgbtIdentifiedFormControl: FormControl<string>;
  lgbtIdentifiedReset: string = null;

  destroyed$ = new Subject<void>();
  irisButtonTypes = IrisButtonTypes;

  readonly userDataLoaded = signal<boolean>(false);
  readonly genderAndEthnicityDataLoaded = signal<boolean>(false);
  readonly loaded = computed<boolean>(() => this.userDataLoaded() && this.genderAndEthnicityDataLoaded());

  constructor(
    private formBuilder: FormBuilder,
    private ethnicityService: ProviderEthnicitiesService,
    private snackBar: MatSnackBar,
    private refDataService: ReferenceDataHttpService,
    private userService: ProviderUserService,
    private appService: SondermindApplicationService,
    private personaProviderStore: PersonaProviderStore
  ) { }

  ngOnInit(): void {
    this.loadData();
    this.loadUser();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.genderForm?.pristine && this.ethnicityForm?.pristine && this.lgbtIdentifiedFormControl?.pristine;
  }

  getEthnicityObservable(): Observable<ReferenceDatum[]> {
    return this.refDataService.list(ReferenceDataTypes.ethnicities, {
      filter: [{ key: 'hidden', value: 'false' }],
      sort: [{ key: 'title', asc: true }],
      page: 'disabled'
    }).pipe(
      map((ethnicities) => ethnicities.data.filter((e) => e.title !== 'Other')),
      map((ethnicities) => ethnicities.sort((a, b) => {
        if (!a.externalTag) return 1;
        if (!b.externalTag) return -1;
        return 0;
      }))
    );
  }

  loadData(): void {
    forkJoin({
      filteredEthnicities: this.getEthnicityObservable(),
      userEthnicities: this.ethnicityService.index(),
      genders: this.getGenderObservable()
    }).subscribe((results) => {
      this.filteredEthnicites = results.filteredEthnicities;
      this.userEthnicities = results.userEthnicities.data;
      this.genderOptions = results.genders.map((gender) => ({
        name: gender.title,
        value: gender.id
      }));

      this.initializeEthnicityForm();

      this.genderAndEthnicityDataLoaded.set(true);
    });
  }

  loadUser(): void {
    combineLatest([
      this.appService.currentUser$,
      this.personaProviderStore.getPersonaProviderProfile$()
    ]).pipe(
      filter(([user, profile]) => !!user && !!profile),
      takeUntil(this.destroyed$)
    ).subscribe(([user, profile]) => {
      this.user = user;
      this.providerProfile = profile;

      if (!this.genderForm) {
        this.initializeGenderForm();
      }
      if (!this.lgbtIdentifiedFormControl) {
        this.initializeLgbtForm();
      }

      this.userDataLoaded.set(true);
    });
  }

  initializeGenderForm(): void {
    this.genderForm = new FormGroup<IGenderForm>({
      id: new FormControl(this.user?.id, [Validators.required]),
      genderId: new FormControl(this.user?.genderId, [Validators.required])
    });

    this.genderForm.markAllAsTouched();
    this.genderForm.updateValueAndValidity();
  }

  getGenderObservable(): Observable<ReferenceDatum[]> {
    return this.refDataService.list(
      ReferenceDataTypes.genders,
      { sort: { key: 'title', asc: true } }
    ).pipe(
      map((resp) => resp.data)
    );
  }

  initializeEthnicityForm(): void {
    this.ethnicityForm = this.formBuilder.group({
      ethnicities: this.formBuilder.group({})
    });

    const ethnicityFormGroup = this.ethnicityForm.controls.ethnicities as FormGroup;
    ethnicityFormGroup.addValidators([Validators.required, this.atLeastOneSelected()]);

    this.filteredEthnicites.forEach((ethnicity) => {
      const isSelected = this.userEthnicities.some((e) => e.ethnicity.id === ethnicity.id);
      const ctrl = this.formBuilder.control(isSelected);
      ethnicityFormGroup.addControl(ethnicity.id.toString(), ctrl);
    });
    this.ethnicityFormReset = this.ethnicityForm.value;

    // mark as touched so error shows up right away
    ethnicityFormGroup.markAllAsTouched();
    ethnicityFormGroup.updateValueAndValidity();
  }

  initializeLgbtForm(): void {
    if (!this.lgbtIdentifiedFormControl) {
      this.lgbtIdentifiedFormControl = new FormControl(
        this.providerProfile?.lgbtIdentified ?? null,
        [Validators.required]
      );
      this.lgbtIdentifiedReset = this.lgbtIdentifiedFormControl.value;

      this.lgbtIdentifiedFormControl.markAllAsTouched();
      this.lgbtIdentifiedFormControl.updateValueAndValidity();
    }
  }

  atLeastOneSelected(): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {
      const formControlValues = control?.value as Record<string, boolean>;
      if (!formControlValues) return null;

      const values = Object.values(formControlValues);
      if (!values?.length) return null;

      if (values?.every((x) => !x)) return { needsOne: null };
      return null;
    };
  }

  onCancelAll(): void {
    this.lgbtIdentifiedFormControl.patchValue(this.providerProfile?.lgbtIdentified ?? null);
    this.lgbtIdentifiedFormControl.markAsPristine();

    this.genderForm.patchValue({
      genderId: this.user?.genderId
    });
    this.genderForm.markAsPristine();

    this.ethnicityForm.patchValue(this.ethnicityFormReset);
    this.ethnicityForm.markAsPristine();
  }

  ethnicitySave$(): Observable<IAPIFetchResponse<EthnicityDatum[]>> {
    const formValues = this.ethnicityForm?.controls?.ethnicities?.value as Record<string, boolean>;
    const savedEthnicities: Array<{ id: number; }> = Object.keys(formValues)
      .filter((x) => formValues[x])
      .map((x) => ({
        id: +x
      }));

    return this.ethnicityService.update(
      savedEthnicities,
      this.user.personaProviderId
    );
  }

  lgbtIdentifiedSave$(): Observable<PersonaProviderProfile> {
    return this.personaProviderStore.updateLgbtIdentified$(
      this.lgbtIdentifiedFormControl.value,
      this.user.personaProviderId
    );
  }

  genderSave$(): Observable<ServerResponse<ProviderUser>> {
    return this.userService.update(<PartialProviderWithId> this.genderForm.value);
  }

  onSaveAll(): void {
    if (this.saving || this.lgbtIdentifiedFormControl.invalid || this.genderForm.invalid
      || this.ethnicityForm.invalid
    ) {
      this.snackBar.open(
        'Error saving. Please check required fields on Identity.',
        'Dismiss',
        { duration: 30000, panelClass: 'toast-color' }
      );
      return;
    }

    this.saving = true;

    forkJoin([
      this.ethnicitySave$(),
      this.lgbtIdentifiedSave$(),
      this.genderSave$()
    ]).subscribe(() => {
      this.ethnicityFormReset = this.ethnicityForm.value;
      this.lgbtIdentifiedReset = this.lgbtIdentifiedFormControl.value;

      this.ethnicityForm.markAsPristine();
      this.genderForm.markAsPristine();
      this.lgbtIdentifiedFormControl.markAsPristine();

      this.appService.reloadCurrentUser();
      this.snackBar.open('Your identity selections have been saved.');
      this.saving = false;
    });
  }
}
